/* Cmd+Z for a care-plan questionnaire answer decision.
 *
 * The decision is a FHIR write, invisible to the editor's Yjs UndoManager, so
 * the key has to be caught outside the editor — but never taken FROM it. Two
 * guards keep the split clean:
 *
 * - a key the editor's keymap handled arrives here already default-prevented,
 *   because ProseMirror prevents the default of every key it consumes, so an
 *   undo the note editor performed is never doubled by an answer reversal;
 * - a typing context owns its own undo. `isContentEditable` is what separates
 *   them: it is true while the clinician edits inside an island or the note
 *   body, and false inside the questionnaire node view, which the editor
 *   renders `contenteditable="false"`.
 *
 * One listener serves every mounted questionnaire — several are on the page at
 * once and the stack they share is global, so per-instance listeners would
 * reverse one decision once per questionnaire. */

import { useEffect } from 'react'
import {
  redoQuestionnaireDecision,
  undoQuestionnaireDecision,
} from '#src/hooks/useQuestionnaireDecisionUndoStore.ts'

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false
  }
  return (
    target.isContentEditable ||
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    target.tagName === 'SELECT'
  )
}

function onKeyDown(event: KeyboardEvent): void {
  if (!(event.metaKey || event.ctrlKey) || event.altKey) {
    return
  }
  const key = event.key.toLowerCase()
  if (key !== 'z' && key !== 'y') {
    return
  }
  if (event.defaultPrevented || isTypingTarget(event.target)) {
    return
  }
  const acted =
    key === 'y' || event.shiftKey
      ? redoQuestionnaireDecision()
      : undoQuestionnaireDecision()
  if (acted) {
    event.preventDefault()
  }
}

let subscribers = 0

export function useQuestionnaireDecisionUndoHotkey(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) {
      return
    }
    subscribers += 1
    if (subscribers === 1) {
      document.addEventListener('keydown', onKeyDown)
    }
    return () => {
      subscribers -= 1
      if (subscribers === 0) {
        document.removeEventListener('keydown', onKeyDown)
      }
    }
  }, [enabled])
}
